import { Form } from 'formik';
import styled from 'styled-components';

// Fills the tab panel so the sticky action bar pins to the bottom of the card
export const SettingsForm = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

export const PageGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  grid-template-rows: 1fr auto;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)};
  align-items: start;
  flex: 1;
  min-height: 0;
`;

export const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const PreviewColumn = styled.aside`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: ${({ theme }) => theme.spacing(3)};
  min-width: 0;
`;

export const PreviewPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 480px;
  border: 1px dashed ${({ theme }) => theme.palette.divider};
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.grey[100]};
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 14px;
  text-align: center;
  padding: ${({ theme }) => theme.spacing(4)};
`;

export const PreviewTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(1)};

  svg {
    font-size: 14px;
  }
`;

export const PreviewLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

export const PreviewCanvas = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px 12px;
  background: ${({ theme }) => theme.palette.grey[100]};
  border-radius: 8px;
  max-height: 70vh;
  overflow: auto;
`;

export const PreviewPage = styled.div`
  background: ${({ theme }) => theme.palette.common.white};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  font-size: 8px;
  color: ${({ theme }) => theme.palette.text.secondary};
  overflow: hidden;
`;

export const PreviewHeader = styled.div`
  flex: 0 0 auto;
  line-height: 1.2;
  word-break: break-word;

  p {
    margin: 0;
  }
`;

export const PreviewFooter = styled(PreviewHeader)`
  margin-top: auto;
`;

export const PreviewPageBody = styled.div`
  flex: 1 1 auto;
  padding-top: 6px;
`;

export const PreviewSampleHeading = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.palette.text.primary};
  margin-bottom: 2px;

  & + p {
    margin-top: 0;
  }
`;

export const PreviewSampleText = styled.p`
  margin: 0 0 6px;
  line-height: 1.3;
`;

export const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const StatusBanner = styled.div`
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2)};
  border-radius: 8px;
  background: ${({ theme }) => theme.tint.warning.medium};
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 14px;
`;

// Inline caution shown inside the Layout card when Pages is selected.
export const InlineWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1)};
  margin: 0 ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2)};
  border-radius: 8px;
  background: ${({ theme }) => theme.tint.warning.medium};
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 13px;
  line-height: 1.4;

  svg {
    flex-shrink: 0;
    font-size: 18px;
    color: ${({ theme }) => theme.palette.warning.main};
  }
`;

// Bulleted limitations list rendered inside the switch-to-pages confirmation.
export const LimitationsList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(2.5)};
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 14px;

  li + li {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
  }
`;

export const ModalNote = styled.p`
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 14px;
`;

// Section card --------------------------------------------------------------
export const Card = styled.section`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.background.paper};
  overflow: hidden;
`;

export const CardHeader = styled.header`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.palette.grey[100]};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

export const CardHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
  flex: 1;
  min-width: 0;
`;

export const CardHeaderAction = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const CardTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const CardDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
`;

// Setting row ---------------------------------------------------------------
export const Row = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};

  & + &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${({ theme }) => theme.spacing(3)};
    right: ${({ theme }) => theme.spacing(3)};
    border-top: 1px solid ${({ theme }) => theme.palette.divider};
  }
`;

export const RowLabelCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.25)};
  min-width: 0;
`;

export const RowLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const RowHelp = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const RowControlCell = styled.div`
  display: flex;
  min-width: 0;
  justify-content: flex-end;
`;

export const RowControlFill = styled.div`
  width: 100%;
`;

// Multi-input grids inside a control cell (custom size / custom margins / header cols)
export const ControlGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: ${({ $columns }) => `repeat(${$columns}, minmax(0, 1fr))`};
  gap: ${({ theme }) => theme.spacing(1)};
  width: 100%;
`;

export const ControlGridCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

export const ControlGridLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

// Sticky footer -------------------------------------------------------------
export const StickyFooter = styled.div`
  position: sticky;
  bottom: 0;
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  margin: 0 -${({ theme }) => theme.spacing(3)} -${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.palette.background.paper};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  z-index: 1;
`;

export const FooterLeftSlot = styled.div`
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const SaveStatePill = styled.span<{ $dirty: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $dirty, theme }) => ($dirty ? theme.tint.warning.medium : theme.palette.grey[100])};
  color: ${({ theme }) => theme.palette.text.secondary};
`;
